import ModalHistoricoDiaFolha from './ModalHistoricoDia';
import MyContext from '@/shared/contexts/AppContext';
import React, { useContext, useState, useEffect } from 'react';
import { AttendanceSheetDayDto, AttendanceSheetResponseDto } from '@/api.types';
import { AttendanceSheetDayEnum } from '../../enums/attendance-sheet-day.enum';
import { DataAmigavel, dayOfTheWeek } from '@/shared/utils/datas';
import { FileAnexo } from '../components/FileAnexo';
import { fillDayAttendanceSheet } from '../../attendance-sheet.service';
import { isWeekend } from 'date-fns';
import { styles } from '@/modules/business/attendance-sheet/presentation/modals/ModalAttendanceStyle';
import { toast } from 'react-toastify';

enum actionsType {
  clean = 0,
  present = 1,
  miss = 2,
  vacation = 3,
}

interface ModalMotivoFaltaProps {
  isOpen: boolean;
  onConfirm: (modifiedDay: AttendanceSheetDayDto) => void;
  onCancel: () => void;
  folha: AttendanceSheetResponseDto;
  modifiedDay: AttendanceSheetDayDto;
  cargaHorariaMaxima?: number;
}

const ModalMotivoFalta: React.FC<ModalMotivoFaltaProps> = ({ isOpen, onConfirm, onCancel, folha, modifiedDay, cargaHorariaMaxima = 300 }) => {
  const [filledDay, setFilledDay] = useState<AttendanceSheetDayDto>(modifiedDay);
  const [finalAction, setFinalAction] = useState<actionsType>(() => getInitialFinalAction(modifiedDay));
  const [modalHistorico, setModalHistorico] = useState(false);

  const context = useContext(MyContext);
  if (!context)
    throw new Error('Contexto deve ser usado dentro de um Provedor.');

  if (!isOpen)
    return null;

  if (folha.estagio.lotacao && !filledDay.horarioEntrada && !filledDay.horarioSaida) {
    filledDay.horarioEntrada = folha.estagio.lotacao?.entrada
    filledDay.horarioSaida = folha.estagio.lotacao?.saida
  }

  const dia = new Date(folha.ano, folha.mes - 1, modifiedDay.dia);

  function getInitialFinalAction(day: AttendanceSheetDayDto): actionsType {
    switch (day.status) {
      case AttendanceSheetDayEnum.PRESENTE:
        return actionsType.present;
      case AttendanceSheetDayEnum.FALTA_JUSTIFICADA:
      case AttendanceSheetDayEnum.FALTA_NAO_JUSTIFICADA:
        return actionsType.miss;
      case AttendanceSheetDayEnum.FERIAS:
        return actionsType.vacation;
      default:
        return actionsType.clean;
    }
  }

  useEffect(() => {
    if (isOpen) {
      const normalizedDay = {
        ...modifiedDay,
        anexo: modifiedDay.anexo
          ? (modifiedDay.anexo as any).data
            ? (modifiedDay.anexo as any).data
            : modifiedDay.anexo
          : undefined,
      };

      // Só preenche automaticamente se o dia estiver indefinido/limpo
      // e se for o estagiário logado
      if (context?.state.cpf === folha.estagiario?.cpf &&
        folha.estagio.lotacao &&
        (!normalizedDay.horarioEntrada && !normalizedDay.horarioSaida) &&
        normalizedDay.status === AttendanceSheetDayEnum.INDEFINIDO) {

        normalizedDay.horarioEntrada = folha.estagio.lotacao.entrada;
        // Não preenche automaticamente a saída - deixa como pendente
        // normalizedDay.horarioSaida = folha.estagio.lotacao.saida; // ← Remove esta linha
      }

      setFilledDay(normalizedDay);
      setFinalAction(getInitialFinalAction(normalizedDay));
    }
  }, [isOpen, modifiedDay, context?.state.cpf, folha.estagiario?.cpf, folha.estagio.lotacao]);

  useEffect(() => {
    if (isOpen) {
      const normalizedDay = {
        ...modifiedDay,
        anexo: modifiedDay.anexo
          ? (modifiedDay.anexo as any).data
            ? (modifiedDay.anexo as any).data
            : modifiedDay.anexo
          : undefined,
      };

      setFilledDay(normalizedDay);
      setFinalAction(getInitialFinalAction(normalizedDay));
    }

  }, [isOpen, modifiedDay]);


  function getButtonText(): string {
    if (!context || context.state.cpf !== folha.estagiario?.cpf) {
      return '';
    }

    const hasEntrada = !!filledDay.horarioEntrada;
    const hasSaida = !!filledDay.horarioSaida;

    if (finalAction === actionsType.present) {
      if (hasEntrada && !hasSaida) {
        return 'Salvar como Pendente';
      } else if (hasEntrada && hasSaida) {
        return 'Confirmar Presença';
      }
    }

    switch (finalAction) {
      case actionsType.clean:
        return 'Limpar Dia';
      case actionsType.present:
        return 'Confirmar Presença';
      case actionsType.miss:
        return 'Confirmar Ausência';
      case actionsType.vacation:
        return 'Confirmar Recesso';
      default:
        return 'Confirmar';
    }
  }

  function validateFilledHours(): boolean {
    const { horarioEntrada, horarioSaida } = filledDay;

    // Se tem horário de saída, valida ambos os horários
    if (horarioSaida) {
      if (!horarioEntrada) {
        toast.warning('Por favor, preencha o horário de entrada');
        return false;
      }

      const [entradaH, entradaM] = horarioEntrada.split(':').map(Number);
      const [saidaH, saidaM] = horarioSaida.split(':').map(Number);

      const entradaMin = entradaH * 60 + entradaM;
      const saidaMin = saidaH * 60 + saidaM;

      if (saidaMin <= entradaMin) {
        toast.warning('O horário de saída deve ser após o horário de entrada');
        return false;
      }

      const duracao = saidaMin - entradaMin;
      const cargaComTolerancia = cargaHorariaMaxima + 60;

      if (duracao > cargaComTolerancia) {
        toast.error(`Carga horária diária ultrapassada! Máximo permitido: ${Math.floor(cargaComTolerancia / 60)}h${cargaComTolerancia % 60}m`);
        return false;
      }
    }
    // Se tem apenas horário de entrada, está OK (status será PENDENTE)
    else if (horarioEntrada) {
      // Validação básica do horário de entrada
      const [entradaH, entradaM] = horarioEntrada.split(':').map(Number);
      if (entradaH > 23 || entradaM > 59) {
        toast.warning('Horário de entrada inválido');
        return false;
      }
    }
    // Se não tem nenhum horário, mas está tentando marcar como PRESENTE
    else if (finalAction === actionsType.present) {
      toast.warning('Por favor, preencha pelo menos o horário de entrada');
      return false;
    }

    return true;
  }

  function validadeHoliday(): boolean {
    if (folha.estagio.lotacao?.diaslivres)
      return true;

    const date = new Date(folha.ano, folha.mes - 1, filledDay.dia);
    const isWeekendDay = isWeekend(date);

    const isWeekendWorker = folha.estagio.fim_semana;

    if (isWeekendWorker) {
      if (!isWeekendDay && !filledDay.feriado) {
        toast.warning(
          `O dia ${filledDay.dia}, ${dayOfTheWeek(date)}, não é dia de trabalho (final de semana).`,
        );
        return false;
      }
    } else {
      if (!filledDay.util) {
        toast.warning(
          `O dia ${filledDay.dia}, ${dayOfTheWeek(date)}, não é dia útil.`,
        );
        return false;
      }
    }

    if (filledDay.feriado) {
      toast.warning(`${filledDay.feriado} não é dia de trabalho.`);
      return false;
    }

    return true;
  }

  async function handleSubmit() {
    if (finalAction == actionsType.present && !validateFilledHours())
      return;

    if (!validadeHoliday())
      return;

    const updatedDay: AttendanceSheetDayDto = {
      ...filledDay,
      dia: modifiedDay.dia,
    };

    // Converte strings vazias para undefined
    if (updatedDay.horarioEntrada === '') {
      updatedDay.horarioEntrada = undefined;
    }
    if (updatedDay.horarioSaida === '') {
      updatedDay.horarioSaida = undefined;
    }

    // Determina o status baseado nos horários preenchidos
    if (finalAction === actionsType.present) {
      if (updatedDay.horarioEntrada && !updatedDay.horarioSaida) {
        updatedDay.status = AttendanceSheetDayEnum.PENDENTE;
      } else if (updatedDay.horarioEntrada && updatedDay.horarioSaida) {
        updatedDay.status = AttendanceSheetDayEnum.PRESENTE;
      }
    }

    switch (finalAction) {
      case actionsType.clean:
        updatedDay.anexo = undefined;
        updatedDay.nomeArquivo = undefined;
        updatedDay.horarioEntrada = undefined;
        updatedDay.horarioSaida = undefined;
        break;
      case actionsType.miss:
        updatedDay.horarioEntrada = undefined;
        updatedDay.horarioSaida = undefined;
        break;
      case actionsType.present:
        updatedDay.justificativa = undefined;
        break;
    }

    try {
      await fillDayAttendanceSheet(folha.id, [updatedDay]);
      onConfirm(updatedDay);
    } catch (ex) {
      toast.error(
        ex ? `${ex}` : `Erro ao registrar o dia ${DataAmigavel(dia)}.`,
      );
    }
  }

  function handleStatusChange(action: actionsType) {
    setFinalAction(action);

    if (action === actionsType.clean) {
      setFilledDay({ ...filledDay, status: AttendanceSheetDayEnum.INDEFINIDO });
    } else if (action === actionsType.present) {
      setFilledDay({ ...filledDay, status: AttendanceSheetDayEnum.PRESENTE });
    } else if (action === actionsType.miss) {
      const faltaStatus = filledDay.justificativa?.trim()
        ? AttendanceSheetDayEnum.FALTA_JUSTIFICADA
        : AttendanceSheetDayEnum.FALTA_NAO_JUSTIFICADA;
      setFilledDay({ ...filledDay, status: faltaStatus });
    } else if (action === actionsType.vacation) {
      setFilledDay({ ...filledDay, status: AttendanceSheetDayEnum.FERIAS });
    }
  }

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <h3 style={styles.modalTitle}>
          Registro de Presença - {DataAmigavel(dia)} - {dayOfTheWeek(dia)}
        </h3>

        <div style={styles.actionButtons} className="mb-4">
          {context.state.cpf === folha.estagiario?.cpf && (
            <>
              <button
                title="Registra o dia como falta."
                style={
                  finalAction === actionsType.miss
                    ? styles.absentButton
                    : styles.unselectedButton
                }
                onClick={() => handleStatusChange(actionsType.miss)}
              >
                AUSENTE
              </button>

              <button
                title="Registra o dia como presente."
                style={
                  finalAction === actionsType.present
                    ? styles.presentButton
                    : styles.unselectedButton
                }
                onClick={() => handleStatusChange(actionsType.present)}
              >
                PRESENTE
              </button>
              <button
                title="Registra o dia como recesso."
                style={
                  finalAction === actionsType.vacation
                    ? styles.vacationButton
                    : styles.unselectedButton
                }
                onClick={() => handleStatusChange(actionsType.vacation)}
              >
                RECESSO
              </button>
            </>
          )}
        </div>

        <div style={styles.buttonsContainer}>
          <div>
            {/* {finalAction == actionsType.clean && (
              <span>Limpa a edição do dia selecionado.</span>
            )} */}

            {finalAction === actionsType.present && (
              <div style={styles.timeContainer}>
                <div style={styles.timeInputContainer}>
                  {/* <span style={styles.timeLabel}>Entrada</span>
                  <input
                    type="time"
                    value={filledDay.horarioEntrada || ''}
                    onChange={(e) =>
                      setFilledDay({
                        ...filledDay,
                        horarioEntrada: e.target.value,
                      })
                    }
                    style={styles.timeInput}
                  />
                  <span style={styles.timeLabel}>Saída</span>
                  <input
                    type="time"
                    value={filledDay.horarioSaida || ''}
                    onChange={(e) =>
                      setFilledDay({
                        ...filledDay,
                        horarioSaida: e.target.value,
                      })
                    }
                    style={styles.timeInput}
                  /> */}
                  {finalAction === actionsType.present && (
                    <div style={styles.timeContainer}>
                      <div style={styles.timeInputContainer}>
                        <span style={styles.timeLabel}>Entrada</span>
                        <input
                          type="time"
                          value={filledDay.horarioEntrada || ''}
                          onChange={(e) =>
                            setFilledDay({
                              ...filledDay,
                              horarioEntrada: e.target.value,
                            })
                          }
                          style={styles.timeInput}
                        />
                        <span style={styles.timeLabel}>Saída</span>
                        <input
                          type="time"
                          value={filledDay.horarioSaida || ''}
                          onChange={(e) =>
                            setFilledDay({
                              ...filledDay,
                              horarioSaida: e.target.value,
                            })
                          }
                          style={styles.timeInput}
                        />
                      </div>

                      <div style={styles.timeActionButtons}>
                        {folha.estagio.lotacao && (
                          <button
                            type="button"
                            style={styles.secondaryButton}
                            onClick={() => {
                              setFilledDay({
                                ...filledDay,
                                horarioEntrada: folha.estagio.lotacao?.entrada,
                                horarioSaida: folha.estagio.lotacao?.saida,
                              });
                            }}
                          >
                            Usar Horário Padrão
                          </button>
                        )}

                        <button
                          type="button"
                          style={styles.secondaryButton}
                          onClick={() => {
                            setFilledDay({
                              ...filledDay,
                              horarioSaida: '',
                            });
                          }}
                          disabled={!filledDay.horarioSaida}
                        >
                          Remover Saída
                        </button>
                      </div>

                      {/* Indicador de status */}
                      {filledDay.horarioEntrada && (
                        <div style={{
                          color: filledDay.horarioSaida ? '#4caf50' : '#ff9800',
                          fontSize: '13px',
                          marginTop: '5px',
                          fontWeight: 'bold'
                        }}>
                          Status: {filledDay.horarioSaida ? 'PRESENTE (completo)' : 'PENDENTE (sem saída)'}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div style={styles.reasonContainer}>
            {finalAction == actionsType.miss && <textarea
              value={filledDay.justificativa || ''}
              placeholder="Motivo da falta..."
              rows={3}
              style={styles.textarea}
              onChange={(e) => {
                const novaJustificativa = e.target.value;
                const novoStatus = novaJustificativa.trim()
                  ? AttendanceSheetDayEnum.FALTA_JUSTIFICADA
                  : AttendanceSheetDayEnum.FALTA_NAO_JUSTIFICADA;
                setFilledDay({
                  ...filledDay,
                  justificativa: novaJustificativa,
                  status: finalAction === actionsType.miss ? novoStatus : filledDay.status,
                });
              }}
            />
            }

            <FileAnexo
              cpfUsuario={context.state.cpf}
              cpfEstagiario={folha.estagiario?.cpf}
              cpfResponsavel={folha.responsavel.cpf}
              anexo={filledDay.anexo}
              nomeArquivo={filledDay.nomeArquivo}
              onChange={(anexo, nomeArquivo) =>
                setFilledDay((prev) => ({ ...prev, anexo, nomeArquivo }))
              }
            />
          </div>
        </div>

        <div>
          <button style={styles.cancelButton} onClick={() => { setModalHistorico(true); }} className='float-start'>
            Histórico
          </button>
        </div>
        <div style={styles.actionButtons}>
          <button style={styles.cancelButton} onClick={onCancel}>
            Cancelar
          </button>
          <>
            {context.state.cpf === folha.estagiario?.cpf && (
              <button
                style={styles.confirmButton}
                onClick={() => {
                  handleSubmit();
                  onCancel();
                }}
              >
                {getButtonText()}
              </button>
            )}
          </>
        </div>
      </div>

      {modalHistorico && modifiedDay && (
        <ModalHistoricoDiaFolha
          dia={modifiedDay.dia}
          historicos={modifiedDay.historico}
          show={modalHistorico}
          handleHide={() => setModalHistorico(false)}
        />
      )}
    </div>
  );
};

export default ModalMotivoFalta;
